/**
 * Application Root Component
 *
 * Sets up providers and router; bootstraps Supabase auth state.
 */

import { useEffect, useRef } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { router } from './router';
import { queryClient } from './lib/query-client';
import { useAuthStore } from './store/auth-store';
import { supabase } from './lib/supabase';
import { toUser, type ProfileRow } from './features/auth/types';
import { features } from './config/features';

export default function App() {
  const setUser = useAuthStore((state) => state.setUser);
  const clearUser = useAuthStore((state) => state.clearUser);
  const setLoading = useAuthStore((state) => state.setLoading);
  const authenticatedUserId = useRef<string | null>(null);

  useEffect(() => {
    if (!features.auth || !features.supabase) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const hydrate = async (userId: string | null) => {
      if (authenticatedUserId.current !== userId) {
        queryClient.clear();
        authenticatedUserId.current = userId;
      }

      if (!userId) {
        clearUser();
        return;
      }
      const [{ data: userData }, { data: profile }] = await Promise.all([
        supabase.auth.getUser(),
        supabase.from('pm_profiles').select('*').eq('id', userId).maybeSingle(),
      ]);
      if (cancelled) return;
      const user = toUser(userData.user, (profile as ProfileRow | null) ?? null);
      if (user) setUser(user);
      else clearUser();
    };

    const init = async () => {
      setLoading(true);
      try {
        const { data } = await supabase.auth.getSession();
        await hydrate(data.session?.user.id ?? null);
      } catch {
        clearUser();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void init();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      void hydrate(session?.user.id ?? null);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [setUser, clearUser, setLoading]);

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
